import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { taxpayerDetailPath } from "../../Router/constant";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  DownloadIcon,
  EyeIcon,
  FilterIcon,
  MoreIcon,
  PlusIcon,
  SearchIcon,
  SyncIcon,
} from "../../Components/Icons";
import {
  FILTERS,
  SCORE_RANGES,
  TAXPAYERS,
  TOTAL_TAXPAYERS,
} from "./data";
import "./Taxpayers.css";

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

const EMPTY_FILTERS = FILTERS.reduce(
  (acc, filter) => ({ ...acc, [filter.key]: "" }),
  {}
);

// Score colour follows the same thresholds as the risk chips.
const scoreTone = (score) => {
  if (score >= 80) return "good";
  if (score >= 50) return "warning";
  return "critical";
};

/** Page numbers around the current one, with an ellipsis before the last. */
const pageWindow = (current, total) => {
  if (total <= 6) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }
  const start = Math.min(Math.max(current - 2, 1), total - 4);
  const pages = Array.from({ length: 5 }, (_, index) => start + index);
  return [...pages, "…", total];
};

export default function TaxpayerList() {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);

  const setFilter = (key) => (event) => {
    setFilters((prev) => ({ ...prev, [key]: event.target.value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearch("");
    setPage(1);
  };

  const filtersActive =
    search.trim() !== "" || Object.values(filters).some(Boolean);

  // The API returns one page of rows; this filters the mock page in place.
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return TAXPAYERS.filter((taxpayer) => {
      if (
        term &&
        !`${taxpayer.name} ${taxpayer.nif}`.toLowerCase().includes(term)
      ) {
        return false;
      }
      if (filters.status && taxpayer.status !== filters.status) return false;
      if (filters.risk && taxpayer.risk !== filters.risk) return false;
      if (filters.industry && taxpayer.industry !== filters.industry) {
        return false;
      }
      if (filters.province && taxpayer.province !== filters.province) {
        return false;
      }
      if (filters.score) {
        const [min, max] = SCORE_RANGES[filters.score];
        if (taxpayer.score < min || taxpayer.score > max) return false;
      }
      return true;
    });
  }, [search, filters]);

  const totalPages = Math.ceil(TOTAL_TAXPAYERS / perPage);
  const firstRow = (page - 1) * perPage + 1;
  const lastRow = firstRow + rows.length - 1;

  const allSelected = rows.length > 0 && selected.length === rows.length;

  const toggleAll = () =>
    setSelected(allSelected ? [] : rows.map((row) => row.nif));

  const toggleRow = (nif) =>
    setSelected((prev) =>
      prev.includes(nif) ? prev.filter((item) => item !== nif) : [...prev, nif]
    );

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Taxpayer List</h1>
          <p className="page__subtitle">
            View, search and manage all registered taxpayers in the national
            registry.
          </p>
        </div>

        <div className="page__actions">
          <button type="button" className="dgi-btn dgi-btn--ghost">
            <DownloadIcon size={17} />
            Export
          </button>
          <button type="button" className="dgi-btn dgi-btn--primary">
            <PlusIcon size={17} />
            Add Taxpayer
          </button>
        </div>
      </div>

      {/* ------------------------------------------------------ filters -- */}
      <section className="dgi-card filters" aria-label="Filters">
        <div className="filters__grid">
          <div>
            <label className="filters__label" htmlFor="taxpayer-search">
              Search
            </label>
            <div className="filters__search">
              <input
                id="taxpayer-search"
                type="search"
                placeholder="Search by name, NIF or reference..."
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
              <SearchIcon size={17} />
            </div>
          </div>

          {FILTERS.map((filter) => (
            <div key={filter.key}>
              <label className="filters__label" htmlFor={`filter-${filter.key}`}>
                {filter.label}
              </label>
              <span className="select select--block">
                <select
                  id={`filter-${filter.key}`}
                  value={filters[filter.key]}
                  onChange={setFilter(filter.key)}
                >
                  <option value="">{filter.placeholder}</option>
                  {filter.options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon size={16} />
              </span>
            </div>
          ))}
        </div>

        <div className="filters__foot">
          <button type="button" className="dgi-btn dgi-btn--ghost dgi-btn--sm">
            <FilterIcon size={16} />
            More Filters
          </button>
          <button
            type="button"
            className="filters__clear"
            onClick={clearFilters}
            disabled={!filtersActive}
          >
            <SyncIcon size={16} />
            Clear Filters
          </button>
        </div>
      </section>

      {/* -------------------------------------------------------- table -- */}
      <section className="dgi-card">
        <div className="listbar">
          <p className="listbar__count">
            Showing {rows.length === 0 ? 0 : firstRow} to {lastRow} of{" "}
            {TOTAL_TAXPAYERS.toLocaleString("en-US")} taxpayers
          </p>

          <div className="listbar__pager">
            <span className="select">
              <select
                value={perPage}
                aria-label="Rows per page"
                onChange={(event) => {
                  setPerPage(Number(event.target.value));
                  setPage(1);
                }}
              >
                {PER_PAGE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option} per page
                  </option>
                ))}
              </select>
              <ChevronDownIcon size={16} />
            </span>

            <nav className="pager" aria-label="Pagination">
              <button
                type="button"
                className="pager__btn"
                aria-label="First page"
                disabled={page === 1}
                onClick={() => setPage(1)}
              >
                <ChevronsLeftIcon size={16} />
              </button>
              <button
                type="button"
                className="pager__btn"
                aria-label="Previous page"
                disabled={page === 1}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              >
                <ChevronLeftIcon size={16} />
              </button>

              {pageWindow(page, totalPages).map((entry, index) =>
                entry === "…" ? (
                  // eslint-disable-next-line react/no-array-index-key
                  <span className="pager__gap" key={`gap-${index}`}>
                    …
                  </span>
                ) : (
                  <button
                    key={entry}
                    type="button"
                    className={`pager__btn${
                      entry === page ? " pager__btn--active" : ""
                    }`}
                    aria-current={entry === page ? "page" : undefined}
                    onClick={() => setPage(entry)}
                  >
                    {entry.toLocaleString("en-US")}
                  </button>
                )
              )}

              <button
                type="button"
                className="pager__btn"
                aria-label="Next page"
                disabled={page === totalPages}
                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
              >
                <ChevronRightIcon size={16} />
              </button>
              <button
                type="button"
                className="pager__btn"
                aria-label="Last page"
                disabled={page === totalPages}
                onClick={() => setPage(totalPages)}
              >
                <ChevronsRightIcon size={16} />
              </button>
            </nav>
          </div>
        </div>

        <div className="dgi-table-wrap">
          <table className="dgi-table dgi-table--registry">
            <thead>
              <tr>
                <th className="dgi-table__check">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label="Select all rows"
                  />
                </th>
                <th>Taxpayer Name</th>
                <th>NIF</th>
                <th>Status</th>
                <th>Risk Level</th>
                <th>Compliance Score</th>
                <th>Industry</th>
                <th>Province</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((taxpayer) => (
                <tr key={taxpayer.nif}>
                  <td className="dgi-table__check">
                    <input
                      type="checkbox"
                      checked={selected.includes(taxpayer.nif)}
                      onChange={() => toggleRow(taxpayer.nif)}
                      aria-label={`Select ${taxpayer.name}`}
                    />
                  </td>
                  <td>
                    <Link
                      className="link"
                      to={taxpayerDetailPath(taxpayer.nif)}
                    >
                      {taxpayer.name}
                    </Link>
                    <span className="dgi-table__sub">{taxpayer.type}</span>
                  </td>
                  <td>{taxpayer.nif}</td>
                  <td>
                    <span
                      className={`chip chip--dot chip--${taxpayer.status.toLowerCase()}`}
                    >
                      {taxpayer.status}
                    </span>
                  </td>
                  <td>
                    <span className={`chip chip--${taxpayer.risk.toLowerCase()}`}>
                      {taxpayer.risk}
                    </span>
                  </td>
                  <td>
                    <span className="score">
                      <span className="score__value">
                        <strong>{taxpayer.score}</strong> / 100
                      </span>
                      <span className="score__track">
                        <span
                          className={`score__fill score__fill--${scoreTone(
                            taxpayer.score
                          )}`}
                          style={{ width: `${taxpayer.score}%` }}
                        />
                      </span>
                    </span>
                  </td>
                  <td>{taxpayer.industry}</td>
                  <td>{taxpayer.province}</td>
                  <td>
                    {taxpayer.updatedDate}
                    <span className="dgi-table__sub">
                      {taxpayer.updatedTime}
                    </span>
                  </td>
                  <td>
                    <span className="row-actions">
                      <Link
                        className="row-actions__btn"
                        to={taxpayerDetailPath(taxpayer.nif)}
                        aria-label={`View ${taxpayer.name}`}
                      >
                        <EyeIcon size={17} />
                      </Link>
                      <button
                        type="button"
                        className="dgi-card__more"
                        aria-label={`Actions for ${taxpayer.name}`}
                      >
                        <MoreIcon size={18} />
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {rows.length === 0 && (
          <p className="dgi-table__empty">
            No taxpayers match these filters.{" "}
            <button type="button" className="link" onClick={clearFilters}>
              Clear filters
            </button>
          </p>
        )}
      </section>
    </div>
  );
}
